import { PaxCheckRequiredAction } from "api";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import usePaxSession from "features/shared/hooks/usePaxSession";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import ChecklistItemStatus from "models/checklist/checklist-item/checklist-item-status";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import { useAppStore } from "store/store";
import {
  getRedirectURLForItem,
  getRedirectURLForItemType,
} from "./useItemLevelRedirect";

/*
Function returns relative URL for multipax session, based on sessionId, ie
/session/{sessionId}
*/
export function getSessionRelativeUrl(sessionId: string): string {
  const encodedSessionId = encodeURIComponent(sessionId);
  return `/session/${encodedSessionId}`;
}

/*
Function returns relative URL for pax session,
based on sessionId and paxSessionId, ie
/session/{sessionId}/pax/{paxSessionId}
*/
export function getPaxSessionRelativeUrl(
  sessionId: string,
  paxSessionId: string
): string {
  const sessionRelativeUrl = getSessionRelativeUrl(sessionId);
  return `${sessionRelativeUrl}/pax/${encodeURIComponent(paxSessionId)}`;
}

/*
Function returns relative URL for item,
based on sessionId, paxSessionId and itemId, ie
/session/{sessionId}/pax/{paxSessionId}/item/{itemId}
*/
export function getItemRelativeUrl(
  sessionId: string,
  paxSessionId: string,
  itemId: string
): string {
  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSessionId
  );
  return `${paxSessionRelativeUrl}/item/${encodeURIComponent(itemId)}`;
}

/*
Function returns relative URL to questions page in "declarations" regime,
based on sessionId and paxSessionId, ie
/session/{sessionId}/pax/{paxSessionId}/questions
+ isDeclaration: true in state
*/
export function getNavigateToDeclarations(
  sessionId: string,
  paxSessionId: string,
  isEditing: boolean = false
): [url: string, state?: any] {
  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSessionId
  );
  return [
    `${paxSessionRelativeUrl}/questions`,
    {
      isDeclaration: true,
      isEditing: isEditing,
    },
  ];
}

/*
Main hook defining navigation flow for the user.
Provides navigation to next page in the flow.

New flow:
1. session / consent (if consent wasn't provided for session)
2. session / select passenger (if multipax and no pax is selected)
3. session / pax / travel-doc (if no nationality/birthdate provided OR travel doc is not verified)
4. session / pax / questions (only "have document" ones)
5. session / pax / questions ("declaration" - any remaining questions)
6. session / pax / address (for US bound flight)
7. session / pax / next unverified item
8. callback / fallbackRedirect
*/
const useFlowRedirect = (
  fallbackRedirect?: () => void
): [shouldNavigate: boolean, callback: () => void] => {
  const navigate = useNavigateWithQuery();

  const paxSessions = useAppStore((state) => state.paxSessions);
  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();

  const sessionRelativeUrl = getSessionRelativeUrl(sessionId);

  /* 1. redirect to consent page if any pax not yet provided consent response */
  const anyPaxWithoutConsent = paxSessions.find(
    (paxSession) =>
      paxSession.requiredAction ===
      PaxCheckRequiredAction.PROVIDE_CONSENT_RESPONSE
  );

  if (anyPaxWithoutConsent) {
    return [true, () => navigate(`${sessionRelativeUrl}/consent`)];
  }

  /* 
  2. if no pax is selected, redirect to first pax/checklist if there is only one,
  or to pax select screen if multipax
  */
  if (!paxSession) {
    return paxSessions.length === 1
      ? [
          true,
          () =>
            navigate(
              `${getPaxSessionRelativeUrl(
                sessionId,
                paxSessions[0].paxSessionId
              )}/checklist`
            ),
        ]
      : [true, () => navigate(`${sessionRelativeUrl}/select-passenger`)];
  }

  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSession.paxSessionId
  );

  /*
    3. require travel doc
    if nationality or birthdate are still required - this means travel doc was not yet provided,
    redirect to travel doc input page. OR when travel document requires verification.
    */
  if (
    paxSession.requiredAction ===
      PaxCheckRequiredAction.PROVIDE_NATIONALITY_OR_BIRTHDATE ||
    paxSession.isTravelDocumentVerified === false
  ) {
    return [
      true,
      () =>
        navigate({
          pathname: getRedirectURLForItemType(
            sessionId,
            paxSession,
            ChecklistItemType.TravelDoc
          ),
          search: "forceDocumentType=", // clear forceDocumentType
        }),
    ];
  }

  /*
  questions/declaratrions
  */
  const notAnsweredHaveDocumentQuestions =
    paxSession.checklist.questions.filter(
      (q) => q.answer === undefined && q.type === "have-document-question"
    );

  const notAnsweredDeclarationQuestions = paxSession.checklist.questions.filter(
    (q) =>
      // for declarations, pick up only relevant
      q.isRelevant &&
      q.answer === undefined &&
      (q.type === "declaration-item" || q.type === "pick-from-list-question")
  );

  if (
    paxSession.requiredAction === PaxCheckRequiredAction.ANSWER_QUESTIONS ||
    notAnsweredHaveDocumentQuestions.length > 0
  ) {
    if (notAnsweredHaveDocumentQuestions.length > 0) {
      /*
      4. ask all haveDocument + passportTypeIn questions (if any)
      */
      return [
        true,
        () =>
          navigate(`${paxSessionRelativeUrl}/questions`, {
            state: {
              showOnlyHaveDocumentQuestions: true,
            },
          }),
      ];
    }

    /*
    5. ask any questions left in the form of declaration (if any)
    no have document questions requiring answers,
    but there are still questions - send to "declaration" form
    to answer all remaining questions that are not have document
    */
    if (notAnsweredDeclarationQuestions.length > 0) {
      const [url, state] = getNavigateToDeclarations(
        sessionId,
        paxSession.paxSessionId
      );
      return [
        true,
        () =>
          navigate(url, {
            state: state,
          }),
      ];
    }

    // something is weird - no have document questions
    // and no declaration questions left. Let's redirect to full questions page to sort
    return [
      true,
      () =>
        navigate(`${paxSessionRelativeUrl}/questions`, {
          state: {
            showOnlyHaveDocumentQuestions: true,
            isDeclaration: false,
          },
        }),
    ];
  }

  /*
  6. address (US-bound flights)
  */
  if (paxSession.requiredAction === PaxCheckRequiredAction.PROVIDE_ADDRESS) {
    return [true, () => navigate(`${paxSessionRelativeUrl}/address`)];
  }

  /*
    7. verify unresolved items by sort order from api (until all are resolved)
    */
  if (paxSession.requiredAction === PaxCheckRequiredAction.RESOLVE_ITEMS) {
    // pick next unresolved item from the list
    const unresolvedItems = paxSession.checklist.items.filter(
      (item) =>
        item instanceof DocumentRequirement === true &&
        item.status !== ChecklistItemStatus.Resolved &&
        paxSession.checklist.isCoveredByResolvedItems(item) === false
    );

    if (unresolvedItems.length > 0) {
      return [
        true,
        () =>
          navigate({
            pathname: getRedirectURLForItem(
              sessionId,
              paxSession.paxSessionId,
              unresolvedItems[0]
            ),
            search: "forceDocumentType=", // clear forceDocumentType
          }),
      ];
    }
  }

  return fallbackRedirect ? [true, fallbackRedirect] : [false, () => {}];
};

export default useFlowRedirect;
